// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap} from 'rxjs';
import { HttpClient} from '@angular/common/http';
import { GamaRoleConfig} from 'src/app/api/remote-api/.portal.generated/models';
import { getGamaRoleConfig } from 'src/app/api/remote-api/.portal.generated/fn/gama-role-config/get-gama-role-config';
import { MapApiService } from '../../shared/services/map-api.service';
import { getUnauthorizedPageContent } from 'src/app/api/remote-api/.portal.generated/fn/gama-role-config/get-unauthorized-page-content';
import { GRAPH_GROUPS_ENDPOINT, GRAPH_HEADERS } from '../constants/graphApiConstants';

@Injectable({ providedIn: 'root' })
export class AuthService {
  public gamaRoleconfiguration = this.mapApiService.mapEndpoint(getGamaRoleConfig);
  public unauthorizePageContent = this.mapApiService.mapEndpoint(
    getUnauthorizedPageContent,
  );

  constructor(
    private readonly http: HttpClient,
    private readonly mapApiService: MapApiService,
  ) {}

  checkUserAuthorization(): Observable<boolean> {
    return this.getFeatureConfig().pipe(
      switchMap((gamaRoleConfig) => {
        if (!gamaRoleConfig.isEnabled) {
          return of(true);
        }

        const allowedGamaGroups = gamaRoleConfig.groups ?? [];

        if (!allowedGamaGroups.length) {
          return of(true);
        }

        return this.checkUserMembership(allowedGamaGroups).pipe(
          map((isMember) => isMember === true),
          catchError(() => of(true)),
        );
      }),
      catchError(() => of(true)),
    );
  }

  private getFeatureConfig(): Observable<GamaRoleConfig> {
    return this.gamaRoleconfiguration.request().pipe(
      map((response) => ({
        isEnabled: response?.isEnabled ?? false,
        groups: response?.groups ? [...response.groups] : [],
      })),
      catchError(() => of({ isEnabled: false, groups: [] })),
    );
  }

  private checkUserMembership(groupsToCheck: string[]): Observable<boolean> {
    if (!groupsToCheck?.length) {
      return of(true);
    }

    const filterQuery = groupsToCheck
      .map((groupName) => `startswith(displayName,'${groupName}')`)
      .join(' or ');

    const graphUrl =
      `${GRAPH_GROUPS_ENDPOINT}?$count=true&$filter=${filterQuery}`;

    return this.http
      .get<any>(graphUrl, { headers: GRAPH_HEADERS })
      .pipe(
        map((response) => {
          const userGroups = response?.value ?? [];
          return userGroups.length > 0;
        }),
        catchError(() => of(true)),
      );
  }
}
